<modal-dialog #newRoleModal [dialogTemplate]="newRoleDialogTemplate"></modal-dialog>
<ng-template #newRoleDialogTemplate>
  <div class="light-content-panel light-control-host" (keydown.escape)="close()" tabindex="0">
    <div class="modal-header">
      <h2 class="panel-title margin-left">Create Custom Role</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <i class="fa-utility fa-semibold fa-xmark"></i>
      </button>
    </div>
    <form [formGroup]="roleForm" (ngSubmit)="onCreateRole()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- Role Information Section -->
        <div class="form-section">
          <h3>Role Information</h3>
          <div class="role-information-body">
            <div class="flex-row gap-4">
              <div class="form-row flex-1">
                <div class="form-group">
                  <label class="form-label">Role Name</label>
                  <input
                    type="text"
                    formControlName="name"
                    class="arpio-input full-width"
                    placeholder="Your role name..."
                  />
                  @if (nameErrors) {
                    <div class="input-error-text">
                      {{ nameErrors }}
                    </div>
                  }
                </div>
              </div>

              <div class="form-row flex-1">
                <div class="form-group">
                  <label class="form-label"
                    >External ID
                    <em class="optional-label text-xs">(Optional ID used to match SSO role claims)</em></label
                  >
                  <input
                    type="text"
                    formControlName="externalId"
                    class="arpio-input full-width"
                    placeholder="external-role-id (letters, numbers, hyphens, underscores only)"
                  />
                  @if (externalIdErrors) {
                    <div class="input-error-text">
                      {{ externalIdErrors }}
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Policies Section -->
        <div class="form-section">
          <h3>Assign Policies</h3>
          <p class="heading-sub-p-text">
            Create policies to combine a selected access level with a group of applications.
          </p>

          <div formArrayName="policyChildren">
            @for (policyChild of policyChildrenArray.controls; track policyChild; let i = $index) {
              <div [formGroupName]="i" class="policy-child">
                <div class="policy-child-header">
                  <!-- Policy Name and Toggle -->
                  <div class="policy-header-content" (click)="togglePolicyCollapsed(i)">
                    <!-- Collapse Toggle -->
                    <div class="collapse-toggle">
                      <i
                        class="fa-utility fa-semibold"
                        [class.fa-angle-down]="!isPolicyCollapsed(i)"
                        [class.fa-angle-right]="isPolicyCollapsed(i)"
                      ></i>
                    </div>
                    <!-- Policy Name -->
                    <div class="policy-name-display">Policy {{ i + 1 }}</div>
                  </div>
                  <!-- Action Buttons -->
                  <div class="policy-actions">
                    <button
                      type="button"
                      class="icon-button danger"
                      (click)="removePolicyChild(i); $event.stopPropagation()"
                      [disabled]="policyChildrenArray.length <= 1"
                      title="Remove Policy"
                    >
                      <i class="fa-utility-duo fa-semibold fa-trash"></i>
                    </button>
                  </div>
                </div>
                <div class="policy-child-body" [class.collapsed]="isPolicyCollapsed(i)">
                  <!-- Policy Validation Error -->
                  @if (getPolicyErrors(i)) {
                    <div class="input-error-text margin-top">
                      {{ getPolicyErrors(i) }}
                    </div>
                  }
                  <!-- Access Level Selection -->
                  <div class="form-group">
                    <label class="form-label">Access Level</label>
                    <div class="flex gap-4 items-end">
                      <div class="select-wrapper" style="flex: 1">
                        <select class="arpio-select full-width" formControlName="accessLevel">
                          <option value="">Select Access Level</option>
                          @for (level of accessLevels; track level) {
                            <option [value]="level.value">
                              {{ level.label }}
                            </option>
                          }
                        </select>
                      </div>
                    </div>
                  </div>
                  <!-- Application Selection - Only show for app-level permissions -->
                  <div class="application-selection">
                    <div class="flex items-center gap-4 margin-bottom">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        formControlName="allApplications"
                        (change)="onAllApplicationsChange(i, $event.target.checked)"
                      />
                      <label class="checkbox-label">Always Include All Applications</label>
                    </div>
                    <div>OR</div>
                    <div [class.disabled-section]="policyChild.get('allApplications')?.value" class="application-table">
                      <div class="select-applications-header flex-row items-center gap-4 margin-bottom">
                        <div>
                          <label>Select Individual Applications</label>
                        </div>
                        <div class="filter-container">
                          <input
                            pInputText
                            type="text"
                            #appFilter
                            (input)="appTable.filterGlobal(appFilter.value, 'contains')"
                            placeholder="Filter applications..."
                            class="arpio-input filter-input"
                          />
                        </div>
                      </div>
                      <p-table
                        #appTable
                        [value]="availableApplications"
                        [globalFilterFields]="['name', 'type']"
                        dataKey="id"
                        [scrollable]="true"
                        scrollHeight="200px"
                        [tableStyle]="{ 'min-width': '30rem' }"
                        [styleClass]="'p-datatable-sm'"
                      >
                        <ng-template pTemplate="header">
                          <tr>
                            <th class="checkbox-column">
                              <input
                                type="checkbox"
                                class="arpio-checkbox"
                                [checked]="areAllApplicationsSelected(i)"
                                [indeterminate]="areSomeApplicationsSelected(i)"
                                (change)="toggleAllApplications(i, $event)"
                              />
                            </th>
                            <th pSortableColumn="name">Application Name <p-sortIcon field="name"></p-sortIcon></th>
                            @if (azureEnabled) {
                              <th pSortableColumn="type">Cloud Provider <p-sortIcon field="type"></p-sortIcon></th>
                            }
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-application>
                          <tr>
                            <td class="checkbox-column">
                              <input
                                type="checkbox"
                                class="arpio-checkbox"
                                [checked]="isApplicationSelected(i, application)"
                                (change)="toggleApplication(i, application, $event)"
                              />
                            </td>
                            <td>{{ application.name }}</td>
                            @if (azureEnabled) {
                              <td>
                                <span
                                  class="app-type-badge"
                                  [attr.data-type]="application.type.toLowerCase().replace(' ', '-')"
                                >
                                  {{ application.type }}
                                </span>
                              </td>
                            }
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                          <tr>
                            <td colspan="3" class="empty-state-cell text-center">
                              @if (!loadingApplications) {
                                <div class="empty-state-content">
                                  <p class="empty-state-submessage">
                                    Add applications to your account to assign permissions.
                                  </p>
                                </div>
                              }
                              @if (loadingApplications) {
                                <div class="loading-state-content">
                                  <i class="fa-solid fa-spinner fa-spin"></i>
                                  <p class="loading-state-message">Loading applications...</p>
                                </div>
                              }
                            </td>
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="summary">
                          <div class="flex align-items-center justify-content-between">
                            <span
                              >Displaying {{ appTable.filteredValue?.length ?? appTable.value?.length }} of
                              {{ appTable.value?.length }} applications</span
                            >
                          </div>
                        </ng-template>
                      </p-table>
                    </div>
                  </div>
                </div>
              </div>
            }
            <!-- Add Another Policy Link -->
            <div class="margin-top">
              <a class="underline" (click)="addPolicyChild()">
                <i class="fa-utility fa-semibold fa-plus text-sm"></i> Add Another Policy
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="arpio-secondary-button" (click)="close()">CANCEL</button>
        <button type="submit" class="arpio-button" [disabled]="!isFormValid || saving">
          @if (saving) {
            <i class="fa-solid fa-spinner fa-spin"></i>
          }
          {{ getSaveButtonText() }}
        </button>
      </div>
    </form>
  </div>
</ng-template>
